import { Button, TextInput } from 'components/common'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormEvent, useMemo, useState } from 'react'
import { PickerMapping } from 'types/pickerMap'

type Props = {
  index: number
  originalData: PickerMapping
  onSave: (index: number) => void
  onDelete: (index: number) => void
}

const PickerMapItem = ({ index, originalData, onSave, onDelete }: Props) => {
  const {
    register,
    control,
    trigger,
    formState: { errors },
  } = useFormContext<{
    mappings: PickerMapping[]
  }>()

  const pickerName = useWatch({ control, name: `mappings.${index}.pickerName` })
  const pickerId = useWatch({ control, name: `mappings.${index}.pickerId` })

  const [saving, setSaving] = useState(false)

  const isChanged = useMemo(() => {
    return (
      pickerName !== originalData?.pickerName ||
      (pickerId ?? '') !== (originalData?.pickerId ?? '')
    )
  }, [pickerName, pickerId, originalData])

  // Validate only this row; other rows aren't touched
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const isRowValid = await trigger(`mappings.${index}`)

    if (!isRowValid) return

    setSaving(true)
    await onSave(index)
    setSaving(false)
  }

  return (
    <form onSubmit={onSubmit} className="mb-2 grid grid-cols-4 gap-4">
      <TextInput
        name={`mappings.${index}.pickerName`}
        register={register}
        error={errors?.mappings?.[index]?.pickerName?.message}
      />
      <TextInput
        name={`mappings.${index}.pickerId`}
        register={register}
        error={errors?.mappings?.[index]?.pickerId?.message}
      />
      {/* From the picker (Order Tracker), not editable here */}
      <div className="flex items-center px-3 text-sm text-gray-600">
        {originalData?.empId || '-'}
      </div>
      <div className="flex max-h-[42px] items-stretch gap-5">
        <Button
          className="w-full"
          type="submit"
          disabled={!isChanged || saving}
          buttonText="Save"
          loading={saving}
        />
        <Button
          disabled={saving}
          className="w-full"
          type="button"
          onClick={() => onDelete(index)}
          buttonText="Delete"
        />
      </div>
    </form>
  )
}

export default PickerMapItem
